import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

export type TWrapperProps = {
    $direction?: 'x' | 'y',
}

const Wrapper = styled.div<TWrapperProps>`
    display: inline-block;
    float: left;
    max-width: calc(100% - 48px - 12px);
    margin-right: 12px;
    ${({ $direction = 'y' }) => css`
        overflow-${$direction}: hidden;
        scroll-snap-type: ${$direction};
    `}
    cursor: grab;

    scrollbar-width: thin;
    scrollbar-color: ${COLORS.ButtonSecondary};

    /* Works on Chrome/Edge/Safari */

    ::-webkit-scrollbar {
        width: 3px;
        height: 3px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background-color: ${COLORS.ButtonSecondary};
    }
`;

export type TScrollableProps = {
    children: JSX.Element | ReactNode,
    scrollToEnd?: boolean,
}
const Scrollable = ({ children, scrollToEnd = false, ...wrapperProps }: TScrollableProps & TWrapperProps): JSX.Element => {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ left: number, top: number, x: number, y: number }>({
        left: 0,
        top: 0,
        x: 0,
        y: 0,
    });
    const [draggable, setDraggable] = useState(false);

    useEffect(() => {
        if (ref && ref.current !== null && scrollToEnd) {
            ref.current.scrollLeft = ref.current.scrollWidth;
        }
    }, [ref]);

    useEffect(() => {
        if (draggable) {
            document.addEventListener('mousemove', mouseMoveHandler);
            document.addEventListener('mouseup', mouseUpHandler);
        }

        return () => {
            document.removeEventListener('mousemove', mouseMoveHandler);
            document.removeEventListener('mouseup', mouseUpHandler);
        };
    }, [draggable]);

    const mouseMoveHandler = (e: MouseEvent) => {
        const dx = e.clientX - pos.x;
        const dy = e.clientY - pos.y;

        // @ts-ignore
        ref.current.scrollLeft = pos.left - dx;
        // @ts-ignore
        ref.current.scrollTop = pos.top - dy;
    };

    const mouseDownHandler = (e: SyntheticEvent) => {
        if (ref !== null && ref.current !== null) {
            ref.current.style.cursor = 'grabbing';
            ref.current.style.userSelect = 'none';
        }

        setPos({
            left: e.currentTarget.scrollLeft,
            top: e.currentTarget.scrollTop,
            // @ts-ignore
            x: e.clientX,
            // @ts-ignore
            y: e.clientY,
        });
        setDraggable(true);
    };

    const mouseUpHandler = () => {
        setDraggable(false);
        // @ts-ignore
        ref.current.style.cursor = 'grab';
        // @ts-ignore
        ref.current.style.removeProperty('user-select');
    };

    return (
        <Wrapper {...wrapperProps} ref={ref} onMouseDown={mouseDownHandler}>
            {children}
        </Wrapper>
    );
};

export default Scrollable;
